import { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import {
  getPortalClientOrgRolesFromAccess,
  getPortalConsoleOrgRolesFromAccess,
} from '@nx-frontend/features/manage-members';
import { MenuTree, ToggleTitle } from '@nx-frontend/features/menu-tree';
import { IconsMap } from '@nx-frontend/shared/ui/icon';
import { useSearchParams } from '@nx-frontend/shared/hooks/router-proxy/router-history-api';
import { selectSidebarWidgetEvent } from '@nx-frontend/shared/store/customizer-controlbar-store';
import { $sectionRoot } from '@nx-frontend/shared/store/portal-app';
import { $accessConsole, $accessPortal } from '@nx-frontend/shared/store/portal-settings';
import { $sidebarDnd } from '@nx-frontend/shared/store/sidebar-dnd/sidebar-dnd';
import { $userId } from '@nx-frontend/shared/store/user';
import {
  ControlbarEditParams,
  DashboardMenuItemTypes,
  MenuItem,
  MenuItemType,
  MenuTooltipPosition,
  PORTAL_MENU_TYPE,
  TranslatesFnType,
} from '@nx-frontend/shared/types';
import { useExpandPortalMenu, useOpenPortalMenuItem, useSidebarDragOver } from '../hooks';
import { useOpenPortalCreateContextMenu } from '../hooks/portal/open-create-context';
import { useOpenPortalMenuItemContextMenu } from '../hooks/portal/open-menu-context';

export function SidebarPortalMenuSection({
  menuType,
  addDisabled,
}: {
  menuType: PORTAL_MENU_TYPE;
  addDisabled: boolean;
}) {
  const t = useTranslations();
  const [userId, accessPortal, accessConsole, sectionRoot, sidebarDnd] = useUnit([
    $userId,
    $accessPortal,
    $accessConsole,
    $sectionRoot,
    $sidebarDnd,
  ]);
  const [selectSidebarWidget] = useEvent([selectSidebarWidgetEvent]);
  const openPortalMenuItem = useOpenPortalMenuItem();
  const openPortalMenuItemContextMenu = useOpenPortalMenuItemContextMenu();
  const openPortalCreateContextMenu = useOpenPortalCreateContextMenu();
  const dragOver = useSidebarDragOver();

  const toggleTitle = t(`sidebar.toggle-title.${menuType}`);
  const tooltipMap = getTooltipMap(t);
  const availablePortalOrgRoles = useMemo(() => {
    return getPortalClientOrgRolesFromAccess(accessPortal);
  }, [accessPortal]);
  const availableConsoleOrgRoles = useMemo(() => {
    return getPortalConsoleOrgRolesFromAccess(accessConsole);
  }, [accessConsole]);

  const { isMenuExpanded, toggleMenuExpand } = useExpandPortalMenu(menuType);
  const searchParams = useSearchParams();
  const forceActiveId = searchParams.get(ControlbarEditParams.ENTITY_ID) ?? undefined;
  const activeHighlightDisabled =
    (!!searchParams.get(ControlbarEditParams.ENTITY_TYPE) && !forceActiveId) || !sectionRoot.includes('visual');

  const handleMenuHeaderMoreClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleTitleAddClick = (event: MouseEvent) => {
    openPortalCreateContextMenu(null, menuType, event);
  };

  const handleSelectSidebarWidget = useCallback(() => {
    selectSidebarWidget(menuType);
  }, [menuType, selectSidebarWidget]);

  const handleTitleToggle = useCallback(() => {
    toggleMenuExpand({ menuType });
    if (isMenuExpanded) {
      handleSelectSidebarWidget();
    }
  }, [handleSelectSidebarWidget, isMenuExpanded, menuType, toggleMenuExpand]);

  const handleMenuItemClick = useCallback(
    (item: MenuItem) => {
      openPortalMenuItem(item, menuType);
      handleSelectSidebarWidget();
    },
    [handleSelectSidebarWidget, menuType, openPortalMenuItem]
  );

  const onDragOver = (id: string) => {
    dragOver(id, menuType);
  };

  const handleMenuItemAdd = useCallback(
    (item: MenuItem | undefined, event: MouseEvent) => {
      if (item) {
        openPortalCreateContextMenu(item, menuType, event);
        handleSelectSidebarWidget();
      }
    },
    [handleSelectSidebarWidget, menuType, openPortalCreateContextMenu]
  );

  const handleMenuItemMoreClick = (item: MenuItem | undefined, event: MouseEvent, useMousePos?: boolean) => {
    if (item) {
      openPortalMenuItemContextMenu(item, menuType, event, useMousePos);
      handleSelectSidebarWidget();
    }
  };

  const handleTitleClick = () => {
    toggleMenuExpand({ menuType });
    if (isMenuExpanded) {
      handleSelectSidebarWidget();
    }
  };

  return (
    <div>
      <ToggleTitle
        id="sidebar-navigation"
        title={toggleTitle}
        isExpanded={isMenuExpanded}
        moreDisabled={true}
        addDisabled={addDisabled}
        moreTooltip={t('sidebar.sidebar-sidebar_settings')}
        addTooltip={t('sidebar.sidebar-add_items')}
        icon={IconsMap.SIDEBAR_3}
        moreClick={(event) => handleMenuHeaderMoreClick(event)}
        addClick={(event) => handleTitleAddClick(event)}
        toggle={() => handleTitleToggle()}
        itemClick={handleTitleClick}
      />
      {isMenuExpanded && (
        <div>
          <MenuTree
            menuType={menuType}
            selectDisabled={true}
            onMenuItemClick={(item) => handleMenuItemClick(item)}
            tooltipMap={tooltipMap}
            onMenuItemAdd={handleMenuItemAdd}
            onMenuItemMore={handleMenuItemMoreClick}
            onMenuItemRightClick={(menuItem, event) => handleMenuItemMoreClick(menuItem, event, true)}
            hideAddButtonItemTypes={[
              ...DashboardMenuItemTypes,
              MenuItemType.LINK,
              MenuItemType.HOME,
              MenuItemType.PORTAL_PAGE,
              MenuItemType.NOTE,
            ]}
            initMenuItemLevel={1}
            activeDragId={sidebarDnd.activeItemId}
            onDragOver={(id) => onDragOver(id)}
            permissionPreviewEnabled={false}
            userId={userId}
            activeHighlightDisabled={activeHighlightDisabled}
            availablePortalOrgRoles={availablePortalOrgRoles}
            availableConsoleOrgRoles={availableConsoleOrgRoles}
            forceActiveId={forceActiveId}
            hideRootEmptyMessage={true}
          />
        </div>
      )}
    </div>
  );
}

function getTooltipMap(t: TranslatesFnType) {
  return {
    [MenuTooltipPosition.ADD_BUTTON]: {
      [MenuItemType.NOTES_FOLDER]: t('sidebar.sidebar-add_items'),
      [MenuItemType.PORTAL_SECTION]: t('sidebar.sidebar-add_items'),
      [MenuItemType.PORTAL_PROCESS]: t('sidebar.sidebar-add_items'),
    },
    [MenuTooltipPosition.MORE_BUTTON]: {
      [MenuItemType.NOTES_FOLDER]: t('sidebar.sidebar-folder_settings'),
      [MenuItemType.NOTE]: t('sidebar.sidebar-page_settings'),
      [MenuItemType.PORTAL_SECTION]: t('sidebar.sidebar-section_setting'),
      [MenuItemType.PORTAL_PROCESS]: t('sidebar.sidebar-process_setting'),
      [MenuItemType.PORTAL_PAGE]: t('sidebar.sidebar-page_settings'),
      [MenuItemType.LINK]: t('sidebar.sidebar-link_settings'),
    },
  };
}
